import { Card, Descriptions, Spin } from "antd"
import { IPolicyholder } from "platform-client/types"
import React, { useMemo } from "react"
import usePlatform from "contexts/platform/use-platform"
import { useQuery } from "react-query"
import moment from "moment"
import styled from "styled-components"
import { useCompanies } from "contexts/companies/hooks/use-companies"

const StyledDescriptions = styled(Descriptions)`
  .ant-descriptions-row > td {
    width: 25%;
  }
`

export const Summary: React.FC<ISummary> = (props) => {
  const platform = usePlatform()
  const { companiesMap, isLoading: isLoadingCompanies } = useCompanies()

  const accountManagersQuery = useQuery(
    ["accountManagers", platform.token],
    () => platform.organizationManagement.getUsersByRole("Account Manager"),
    {
      enabled: !!platform.token && !!props.client?.ownerID
    }
  )

  const accountManagerName = useMemo(() => {
    if (!props.client?.ownerID || !accountManagersQuery.data) return null
    const accountManager = accountManagersQuery.data.find(
      (user) => user.id === props.client?.ownerID
    )
    return accountManager?.fullName || accountManager?.username || null
  }, [props.client?.ownerID, accountManagersQuery.data])

  const companyName = useMemo(() => {
    if (!props.client?.companyID) return null
    return companiesMap.get(props.client.companyID) || null
  }, [props.client?.companyID, companiesMap])

  const formatContactName = () => {
    if (!props.client?.contact?.name) return "-"

    const name = props.client.contact.name
    const parts = [name.title, name.firstName, name.lastName].filter(Boolean)

    return parts.length > 0 ? parts.join(" ") : "-"
  }

  const formatAddress = () => {
    if (!props.client?.contactAddress) return "-"

    const address = props.client.contactAddress
    const parts = [
      address.line1,
      address.line2,
      address.line3,
      address.locality,
      address.province,
      address.postalCode
    ].filter(Boolean)

    return parts.length > 0 ? parts.join(", ") : "-"
  }

  const formatDateOfBirth = () => {
    if (!props.client?.contact?.dateOfBirth) return "-"

    try {
      const date = moment(props.client.contact.dateOfBirth)
      return date.isValid() ? date.format("MM/DD/yyyy") : "-"
    } catch {
      return props.client.contact.dateOfBirth
    }
  }

  const formatDateCreated = () => {
    if (!props.client?.dateCreated) return "-"

    try {
      const date = moment(props.client.dateCreated)
      return date.isValid() ? date.format("DD/MM/YYYY") : "-"
    } catch {
      return props.client.dateCreated
    }
  }

  return (
    <>
      <Card style={{ marginBottom: "20px" }} loading={props.isLoading}>
        <StyledDescriptions size="small" column={2} bordered>
          {props.client?.businessName && (
            <Descriptions.Item label="Business Name">
              {props.client.businessName}
            </Descriptions.Item>
          )}
          {props.client?.policyholderType && (
            <Descriptions.Item label="Client Type">
              {props.client.policyholderType}
            </Descriptions.Item>
          )}
          {formatContactName() !== "-" && (
            <Descriptions.Item label="Contact Name">
              {formatContactName()}
            </Descriptions.Item>
          )}
          {props.client?.referenceID && (
            <Descriptions.Item label="Client ID">
              {props.client.referenceID}
            </Descriptions.Item>
          )}
          {accountManagerName && (
            <Descriptions.Item label="Account Manager">
              {accountManagerName}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Company Name">
            {isLoadingCompanies ? <Spin size="small" /> : companyName || "-"}
          </Descriptions.Item>
          {formatDateOfBirth() !== "-" && (
            <Descriptions.Item label="Date of Birth">
              {formatDateOfBirth()}
            </Descriptions.Item>
          )}
          {props.client?.contact?.emailAddress && (
            <Descriptions.Item label="Contact Email Address">
              <a href={`mailto:${props.client.contact.emailAddress}`}>
                {props.client.contact.emailAddress}
              </a>
            </Descriptions.Item>
          )}
          {props.client?.contact?.phoneNumber && (
            <Descriptions.Item label="Contact Phone Number">
              <a href={`tel:${props.client.contact.phoneNumber}`}>
                {props.client.contact.phoneNumber}
              </a>
            </Descriptions.Item>
          )}
          {formatAddress() !== "-" && (
            <Descriptions.Item label="Contact Address">
              {formatAddress()}
            </Descriptions.Item>
          )}
          {formatDateCreated() !== "-" && (
            <Descriptions.Item label="Date Created">
              {formatDateCreated()}
            </Descriptions.Item>
          )}
        </StyledDescriptions>
      </Card>
    </>
  )
}

export interface ISummary {
  client?: IPolicyholder
  isLoading: boolean
}
